import { useEffect, useMemo, useState, useCallback } from "react";
import {
  Folder, FolderPlus, FolderOpen, FilePdf, UploadSimple, Trash, PencilSimple,
  Buildings, Users, Car, MagnifyingGlass, X, CircleNotch, Eye, EyeSlash, FileArrowUp, DownloadSimple,
} from "@phosphor-icons/react";
import { downloadDocument } from "../../utils/downloadDocument";
import axios from "../../utils/axiosInstance";
import { vehicleLabel } from '../../utils/vehicleLabel';
import { useAuth } from "../../contexts/AuthContext";
import VaultTree from "./VaultTree";

const API = `${window.location.origin}/api`;

const SCOPES = [
  { key: "firma",     label: "Firmowe",   icon: Buildings, hint: "Umowy, faktury, dokumenty firmy" },
  { key: "pracownik", label: "Pracownicy", icon: Users,     hint: "Dokumenty pracowników" },
  { key: "pojazd",    label: "Pojazdy",   icon: Car,       hint: "Rejestracja, OC, przeglądy" },
];

// ============== Tree helpers ==============
/** Builds a nested tree from a list of "a/b/c" paths. */
function buildTree(folders) {
  const root = { name: "", path: "", children: {}, doc_count: 0 };
  for (const f of folders) {
    const parts = (f.path || "").split("/").filter(Boolean);
    let node = root;
    let acc = "";
    for (const part of parts) {
      acc = acc ? `${acc}/${part}` : part;
      if (!node.children[part]) {
        node.children[part] = { name: part, path: acc, children: {}, doc_count: 0 };
      }
      node = node.children[part];
    }
    node.doc_count = f.doc_count || 0;
    node.folderId = f.id;
    node.auto = !!f.auto;
  }
  return root;
}

function TreeNode(props) { return <VaultTree {...props} />; }
// eslint-disable-next-line no-unused-vars
const _keepTreeNode = TreeNode;


export default function VaultTab() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [scope, setScope] = useState("firma");
  const [entityId, setEntityId] = useState("");
  const [entities, setEntities] = useState([]);
  const [folders, setFolders] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState("");
  const [expanded, setExpanded] = useState(new Set([""]));
  const [loadingFolders, setLoadingFolders] = useState(true);
  const [loadingDocs, setLoadingDocs] = useState(true);

  // Modals
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [renaming, setRenaming] = useState(null); // {oldPath, newName}
  const [uploading, setUploading] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadName, setUploadName] = useState("");
  const [uploadVisible, setUploadVisible] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  // Load entities when scope changes to pracownik/pojazd
  useEffect(() => {
    (async () => {
      if (scope === "pracownik") {
        try {
          const { data } = await axios.get(`${API}/employees`);
          setEntities((data || []).filter((e) => e.role === "employee" || !e.role));
        } catch { setEntities([]); }
      } else if (scope === "pojazd") {
        try {
          const { data } = await axios.get(`${API}/pojazdy`);
          setEntities(data || []);
        } catch { setEntities([]); }
      } else {
        setEntities([]);
      }
      setEntityId("");
      setSelectedFolder("");
    })();
  }, [scope]);

  const loadFolders = useCallback(async () => {
    setLoadingFolders(true);
    try {
      const params = { scope };
      if (entityId) params.entity_id = entityId;
      const { data } = await axios.get(`${API}/vault/folders`, { params });
      setFolders(data || []);
    } catch (e) {
      console.error(e);
      setFolders([]);
    } finally { setLoadingFolders(false); }
  }, [scope, entityId]);

  const loadDocuments = useCallback(async () => {
    setLoadingDocs(true);
    try {
      const params = { scope };
      if (entityId) params.entity_id = entityId;
      // Empty folder selected shows only root-level docs
      params.folder_path = selectedFolder;
      const { data } = await axios.get(`${API}/vault/scoped`, { params });
      setDocuments(data || []);
    } catch (e) {
      console.error(e);
      setDocuments([]);
    } finally { setLoadingDocs(false); }
  }, [scope, entityId, selectedFolder]);

  useEffect(() => {
    if (scope === "pracownik" && !entityId) {
      setFolders([]); setDocuments([]);
      setLoadingFolders(false); setLoadingDocs(false);
      return;
    }
    loadFolders();
    loadDocuments();
  }, [scope, entityId, selectedFolder, loadFolders, loadDocuments]);

  const tree = useMemo(() => buildTree(folders), [folders]);
  const hasFolders = Object.keys(tree.children).length > 0;

  const toggleExpanded = (path) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path); else next.add(path);
      return next;
    });
  };

  // ============== Actions ==============
  const openCreateFolder = () => {
    setNewFolderName(new Date().getFullYear().toString());
    setCreatingFolder(true);
  };

  const handleCreateFolder = async () => {
    const name = (newFolderName || "").trim();
    if (!name) return;
    const path = selectedFolder ? `${selectedFolder}/${name}` : name;
    setSaving(true);
    try {
      await axios.post(`${API}/vault/folders`, {
        scope, path, entity_id: entityId || null,
      });
      setCreatingFolder(false); setNewFolderName("");
      setExpanded((prev) => new Set([...prev, selectedFolder]));
      await loadFolders();
      setSelectedFolder(path);
    } catch (e) {
      alert(e?.response?.data?.detail || "Nie udało się utworzyć folderu");
    } finally { setSaving(false); }
  };

  const handleRenameFolder = async () => {
    if (!renaming) return;
    const newName = (renaming.newName || "").trim();
    if (!newName || newName === renaming.oldName) { setRenaming(null); return; }
    const parts = renaming.oldPath.split("/");
    parts[parts.length - 1] = newName;
    const newPath = parts.join("/");
    setSaving(true);
    try {
      await axios.post(`${API}/vault/folders/rename`, {
        scope, entity_id: entityId || null,
        old_path: renaming.oldPath, new_path: newPath,
      });
      setRenaming(null);
      await loadFolders();
      if (selectedFolder === renaming.oldPath || selectedFolder.startsWith(renaming.oldPath + "/")) {
        setSelectedFolder(selectedFolder.replace(renaming.oldPath, newPath));
      }
    } catch (e) {
      alert(e?.response?.data?.detail || "Nie udało się zmienić nazwy");
    } finally { setSaving(false); }
  };

  const handleDeleteFolder = async (path) => {
    if (!window.confirm(`Usunąć folder "${path}" wraz z zawartością?`)) return;
    try {
      await axios.post(`${API}/vault/folders/delete`, {
        scope, entity_id: entityId || null, path,
      });
      if (selectedFolder === path || selectedFolder.startsWith(path + "/")) setSelectedFolder("");
      await loadFolders();
      await loadDocuments();
    } catch (e) {
      alert(e?.response?.data?.detail || "Nie udało się usunąć folderu");
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) return;
    const fd = new FormData();
    fd.append("scope", scope);
    fd.append("folder_path", selectedFolder || "");
    if (entityId) fd.append("entity_id", entityId);
    fd.append("name", uploadName || uploadFile.name);
    fd.append("visible_to_employee", uploadVisible ? "true" : "false");
    fd.append("file", uploadFile);
    setSaving(true);
    try {
      await axios.post(`${API}/vault/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploading(false);
      setUploadFile(null); setUploadName(""); setUploadVisible(true);
      await loadFolders();
      await loadDocuments();
    } catch (e) {
      alert(e?.response?.data?.detail || "Nie udało się wgrać pliku");
    } finally { setSaving(false); }
  };

  const handleDeleteDoc = async (id) => {
    if (!window.confirm("Usunąć ten dokument?")) return;
    try {
      await axios.delete(`${API}/documents/${id}`);
      await loadDocuments();
      await loadFolders();
    } catch (e) {
      alert(e?.response?.data?.detail || "Nie udało się usunąć");
    }
  };

  const viewDoc = async (id) => {
    try {
      const token = localStorage.getItem("access_token") || "";
      const url = `${API}/documents/${id}?token=${encodeURIComponent(token)}`;
      window.open(url, "_blank", "noopener");
    } catch (e) {
      alert("Nie udało się otworzyć");
    }
  };

  const toggleVisible = async (doc) => {
    const next = !doc.visible_to_employee;
    // Optimistic update
    setDocuments((prev) =>
      prev.map((d) => (d.id === doc.id ? { ...d, visible_to_employee: next } : d))
    );
    try {
      await axios.patch(`${API}/documents/${doc.id}`, { visible_to_employee: next });
    } catch (e) {
      // Rollback on failure
      setDocuments((prev) =>
        prev.map((d) => (d.id === doc.id ? { ...d, visible_to_employee: !next } : d))
      );
      alert(e?.response?.data?.detail || "Nie udało się zmienić widoczności");
    }
  };

  const filteredDocs = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return documents;
    return documents.filter(
      (d) => (d.name || "").toLowerCase().includes(s) || (d.filename || "").toLowerCase().includes(s)
    );
  }, [documents, search]);

  const canWrite = isAdmin || (scope === "pracownik" && entityId === String(user?._id));
  const needsEntityPick = (scope === "pracownik" || scope === "pojazd") && !entityId;

  return (
    <div className="max-w-[1400px] mx-auto px-3 py-4 sm:px-6 sm:py-6" data-testid="vault-container">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h2 className="text-xl sm:text-3xl font-bold text-slate-800 flex items-center gap-2">
            <Folder className="w-6 h-6 text-blue-700" weight="fill" />
            Dokumenty
          </h2>
          <p className="text-xs sm:text-base text-slate-600 mt-1">
            Organizuj dokumenty w folderach — firmowe, pracowników i pojazdów. Automatyczna sortacja po latach.
          </p>
        </div>
      </div>

      {/* Scope tabs */}
      <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
        {SCOPES.map((s) => {
          const Icon = s.icon;
          const active = scope === s.key;
          return (
            <button
              key={s.key}
              onClick={() => setScope(s.key)}
              className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium shrink-0 transition-colors ${
                active ? "bg-blue-700 text-white shadow" : "bg-white border border-slate-200 hover:bg-slate-50 text-slate-700"
              }`}
              data-testid={`vault-scope-${s.key}`}
            >
              <Icon className="w-4 h-4" weight="fill" /> {s.label}
            </button>
          );
        })}
      </div>

      {/* Entity picker for pracownik / pojazd */}
      {(scope === "pracownik" || scope === "pojazd") && (
        <div className="mb-4">
          <label className="text-xs font-medium text-slate-600 block mb-1">
            {scope === "pracownik" ? "Wybierz pracownika" : "Wybierz pojazd"}
          </label>
          <select
            value={entityId}
            onChange={(e) => setEntityId(e.target.value)}
            className="w-full sm:max-w-md px-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
            data-testid="vault-entity-select"
          >
            <option value="">— wybierz —</option>
            {entities.map((e) => (
              <option key={e._id || e.id} value={e._id || e.id}>
                {scope === "pracownik"
                  ? (e.imie_nazwisko || e.name || e.login)
                  : vehicleLabel(e, "?")}
              </option>
            ))}
          </select>
        </div>
      )}

      {needsEntityPick ? (
        <div className="bg-white rounded-xl p-10 text-center text-slate-500 border border-slate-200 shadow-sm">
          Wybierz {scope === "pracownik" ? "pracownika" : "pojazd"} powyżej, aby zobaczyć dokumenty.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
          {/* Sidebar tree */}
          <aside className="bg-white rounded-xl border border-slate-200 shadow-sm p-3 h-max">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-semibold text-slate-500 uppercase">Foldery</div>
              {canWrite && (
                <button
                  onClick={openCreateFolder}
                  className="p-1.5 text-blue-700 hover:bg-blue-50 rounded-md"
                  title="Nowy folder"
                  data-testid="vault-new-folder-btn"
                >
                  <FolderPlus className="w-4 h-4" weight="fill" />
                </button>
              )}
            </div>
            <button
              onClick={() => setSelectedFolder("")}
              className={`w-full text-left flex items-center gap-1 px-2 py-1.5 rounded-md text-sm transition-colors ${
                selectedFolder === "" ? "bg-blue-700 text-white" : "hover:bg-slate-100 text-slate-700"
              }`}
              data-testid="vault-folder-root"
            >
              <span className="w-4" />
              <Folder className="w-4 h-4 shrink-0" weight="fill" /> Wszystkie
            </button>
            {loadingFolders ? (
              <div className="text-center py-4 text-slate-400"><CircleNotch className="w-5 h-5 animate-spin mx-auto" /></div>
            ) : !hasFolders ? (
              <div className="text-xs text-slate-400 italic px-2 py-2">Brak folderów. Utwórz pierwszy.</div>
            ) : (
              <VaultTree
                root={tree}
                selected={selectedFolder}
                onSelect={setSelectedFolder}
                expanded={expanded}
                onToggle={toggleExpanded}
              />
            )}
          </aside>

          {/* File list panel */}
          <section className="bg-white rounded-xl border border-slate-200 shadow-sm">
            {/* Toolbar */}
            <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-slate-100">
              <div className="text-sm text-slate-800 font-semibold min-w-0 flex-1 truncate">
                {selectedFolder ? (
                  <span className="flex items-center gap-1.5">
                    <FolderOpen className="w-4 h-4 text-blue-700" weight="fill" /> {selectedFolder}
                  </span>
                ) : (
                  <span className="text-slate-500">Wszystkie pliki</span>
                )}
              </div>
              <div className="relative">
                <MagnifyingGlass className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Szukaj pliku..."
                  className="pl-8 pr-3 py-1.5 border border-slate-300 rounded-md text-sm w-48"
                  data-testid="vault-search"
                />
              </div>
              {canWrite && selectedFolder && (
                <button
                  onClick={() => setRenaming({ oldPath: selectedFolder, oldName: selectedFolder.split("/").pop(), newName: selectedFolder.split("/").pop() })}
                  className="p-1.5 text-slate-500 hover:bg-slate-100 rounded-md"
                  title="Zmień nazwę folderu"
                  data-testid="vault-rename-folder"
                >
                  <PencilSimple className="w-4 h-4" />
                </button>
              )}
              {canWrite && selectedFolder && (
                <button
                  onClick={() => handleDeleteFolder(selectedFolder)}
                  className="p-1.5 text-red-600 hover:bg-red-50 rounded-md"
                  title="Usuń folder"
                  data-testid="vault-delete-folder"
                >
                  <Trash className="w-4 h-4" />
                </button>
              )}
              {canWrite && (
                <button
                  onClick={() => { setUploading(true); setUploadFile(null); setUploadName(""); setUploadVisible(true); }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-700 hover:bg-blue-800 text-white rounded-md text-xs font-medium"
                  data-testid="vault-upload-btn"
                >
                  <UploadSimple className="w-3.5 h-3.5" weight="bold" /> Wgraj plik
                </button>
              )}
            </div>

            {/* Docs list */}
            {loadingDocs ? (
              <div className="text-center py-12 text-slate-400"><CircleNotch className="w-8 h-8 animate-spin mx-auto" /></div>
            ) : filteredDocs.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                {search ? "Brak wyników." : selectedFolder ? "Pusty folder. Wgraj pierwszy plik." : "Wybierz folder z lewej."}
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {filteredDocs.map((d) => (
                  <div key={d.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50" data-testid={`vault-doc-${d.id}`}>
                    <FilePdf className="w-5 h-5 text-red-600 shrink-0" weight="fill" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-800 truncate">{d.name || d.filename}</div>
                      <div className="text-xs text-slate-500 truncate">
                        {d.filename} · {(d.size / 1024).toFixed(0)} KB
                        {d.upload_date && ` · ${new Date(d.upload_date).toLocaleDateString("pl-PL")}`}
                        {d.folder_path && ` · ${d.folder_path}`}
                      </div>
                    </div>
                    <button
                      onClick={() => viewDoc(d.id)}
                      className="p-1.5 text-slate-500 hover:bg-slate-100 rounded-md"
                      title="Otwórz"
                      data-testid={`vault-view-${d.id}`}
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => downloadDocument(d.id)}
                      className="p-1.5 text-slate-500 hover:bg-slate-100 rounded-md"
                      title="Pobierz"
                      data-testid={`vault-download-${d.id}`}
                    >
                      <DownloadSimple className="w-4 h-4" />
                    </button>
                    {isAdmin && scope === "pracownik" && (
                      <button
                        onClick={() => toggleVisible(d)}
                        className={`p-1.5 rounded-md ${d.visible_to_employee ? "text-emerald-600 hover:bg-emerald-50" : "text-slate-400 hover:bg-slate-100"}`}
                        title={d.visible_to_employee ? "Widoczne dla pracownika — kliknij aby ukryć" : "Ukryte — kliknij aby udostępnić pracownikowi"}
                        data-testid={`vault-visibility-${d.id}`}
                      >
                        {d.visible_to_employee ? (
                          <Eye className="w-4 h-4" weight="fill" />
                        ) : (
                          <EyeSlash className="w-4 h-4" />
                        )}
                      </button>
                    )}
                    {isAdmin && (
                      <button
                        onClick={() => handleDeleteDoc(d.id)}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded-md"
                        title="Usuń"
                        data-testid={`vault-delete-doc-${d.id}`}
                      >
                        <Trash className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      )}

      {/* Create folder modal */}
      {creatingFolder && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6" data-testid="vault-create-folder-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-800">Nowy folder</h3>
              <button onClick={() => setCreatingFolder(false)} className="p-1 hover:bg-slate-100 rounded-md"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-xs text-slate-500 mb-2">
              {selectedFolder ? <>Zostanie utworzony w: <b>{selectedFolder}</b></> : "Nowy folder w katalogu głównym"}
            </p>
            <input
              type="text"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleCreateFolder()}
              placeholder="Nazwa folderu (np. 2026, umowy, faktury)"
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
              autoFocus
              data-testid="vault-create-folder-name"
            />
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setCreatingFolder(false)} className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md text-sm">Anuluj</button>
              <button
                onClick={handleCreateFolder}
                disabled={saving || !newFolderName.trim()}
                className="px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white rounded-md text-sm font-medium disabled:opacity-40"
                data-testid="vault-create-folder-submit"
              >
                {saving ? <CircleNotch className="w-4 h-4 animate-spin" /> : "Utwórz"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rename folder modal */}
      {renaming && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6" data-testid="vault-rename-folder-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-800">Zmień nazwę</h3>
              <button onClick={() => setRenaming(null)} className="p-1 hover:bg-slate-100 rounded-md"><X className="w-5 h-5" /></button>
            </div>
            <input
              type="text"
              value={renaming.newName}
              onChange={(e) => setRenaming({ ...renaming, newName: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && handleRenameFolder()}
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
              autoFocus
              data-testid="vault-rename-input"
            />
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setRenaming(null)} className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md text-sm">Anuluj</button>
              <button
                onClick={handleRenameFolder}
                disabled={saving || !renaming.newName.trim()}
                className="px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white rounded-md text-sm font-medium disabled:opacity-40"
                data-testid="vault-rename-submit"
              >
                {saving ? <CircleNotch className="w-4 h-4 animate-spin" /> : "Zapisz"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload modal */}
      {uploading && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6" data-testid="vault-upload-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-800">Wgraj plik</h3>
              <button onClick={() => setUploading(false)} className="p-1 hover:bg-slate-100 rounded-md"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Folder</label>
                <div className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm text-slate-700 flex items-center gap-1.5">
                  <FolderOpen className="w-4 h-4 text-blue-700" /> {selectedFolder || "Główny"}
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Plik</label>
                <input
                  type="file"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    setUploadFile(f);
                    if (f && !uploadName) setUploadName(f.name.replace(/\.[^.]+$/, ""));
                  }}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                  data-testid="vault-upload-file"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Nazwa (opcjonalnie)</label>
                <input
                  type="text"
                  value={uploadName}
                  onChange={(e) => setUploadName(e.target.value)}
                  placeholder="np. Umowa X"
                  className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                  data-testid="vault-upload-name"
                />
              </div>
              {scope === "pracownik" && isAdmin && (
                <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={uploadVisible}
                    onChange={(e) => setUploadVisible(e.target.checked)}
                    className="w-4 h-4 accent-blue-700"
                    data-testid="vault-upload-visible"
                  />
                  Widoczne dla pracownika
                </label>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setUploading(false)} className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md text-sm">Anuluj</button>
                <button
                  onClick={handleUpload}
                  disabled={saving || !uploadFile}
                  className="px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white rounded-md text-sm font-medium disabled:opacity-40 flex items-center gap-2"
                  data-testid="vault-upload-submit"
                >
                  {saving ? <CircleNotch className="w-4 h-4 animate-spin" /> : <FileArrowUp className="w-4 h-4" />} Wgraj
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
